import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { inventoryService } from '../services/inventoryService';
import { machineService } from '../services/machineService';
import { purchaseOrderService } from '../services/purchaseOrderService';

const LOW_STOCK_THRESHOLD_FIXED = 10;
const LOW_STOCK_THRESHOLD_PERCENTAGE = 0.2;
const API_PAGE_LIMIT = 100;
const MAX_ITEMS_FETCH = 1000;

const isLowStock = (item) => {
  const currentQuantity = item.currentQuantity || 0;
  const purchaseQuantity = item.purchaseQuantity || 0;

  return (
    currentQuantity <= LOW_STOCK_THRESHOLD_FIXED ||
    (purchaseQuantity > 0 && currentQuantity < purchaseQuantity * LOW_STOCK_THRESHOLD_PERCENTAGE)
  );
};

const extractPaginationTotal = (response) => {
  if (!response) return 0;

  if (typeof response.pagination?.total_count === 'number') {
    return response.pagination.total_count;
  }

  if (typeof response.meta?.pagination?.total === 'number') {
    return response.meta.pagination.total;
  }

  if (typeof response.meta?.total === 'number') {
    return response.meta.total;
  }

  return Array.isArray(response.data) ? response.data.length : 0;
};

const fetchInventoryItemsForDashboard = async () => {
  const items = [];
  let offset = 0;

  while (items.length < MAX_ITEMS_FETCH) {
    const remaining = MAX_ITEMS_FETCH - items.length;
    const limit = Math.min(API_PAGE_LIMIT, remaining);
    const response = await inventoryService.getInventoryItems({ limit, offset });
    const batch = response?.data || [];

    if (batch.length === 0) {
      break;
    }

    items.push(...batch);

    if (batch.length < limit) {
      break;
    }

    offset += limit;
  }

  return items;
};

const fetchMachinesForDashboard = async () => {
  const machines = [];
  let page = 1;
  let totalFromMeta;

  while (machines.length < MAX_ITEMS_FETCH) {
    const remaining = MAX_ITEMS_FETCH - machines.length;
    const perPage = Math.min(API_PAGE_LIMIT, remaining);

    const response = await machineService.getMachines({
      page,
      per_page: perPage
    });

    const batch = response?.data || [];
    if (typeof response?.total === 'number') {
      totalFromMeta = response.total;
    }

    if (batch.length === 0) {
      break;
    }

    machines.push(...batch);

    if (
      (typeof totalFromMeta === 'number' && machines.length >= totalFromMeta) ||
      batch.length < perPage
    ) {
      break;
    }

    page += 1;
  }

  return machines;
};

const fetchPurchaseOrderCount = async (status) => {
  const response = await purchaseOrderService.getPurchaseOrders({
    limit: 1,
    page: 1,
    status
  });

  return extractPaginationTotal(response);
};

const fetchRecentPurchaseOrders = async () => {
  const response = await purchaseOrderService.getPurchaseOrders({
    limit: Math.min(10, API_PAGE_LIMIT),
    page: 1
  });

  return response?.data || [];
};

export const useDashboardStats = () => {
  const { accessibleFeatures } = useAuth();
  
  const [stats, setStats] = useState({
    totalProducts: 0,
    lowStockItems: 0,
    pendingPOs: 0,
    activeMachines: 0
  });
  const [recentPOs, setRecentPOs] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track detailed states per category to handle partial permission issues
  const [categoryStates, setCategoryStates] = useState({
    inventory: { loading: true, error: null, hasAccess: true },
    machine: { loading: true, error: null, hasAccess: true },
    purchaseOrder: { loading: true, error: null, hasAccess: true }
  });

  const calculateStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    // 1. Fetch Inventory Data (Total Products & Low Stock)
    const loadInventory = async () => {
      if (!accessibleFeatures['Inventory']) {
        setCategoryStates(prev => ({ ...prev, inventory: { loading: false, error: null, hasAccess: false } }));
        return;
      }

      try {
        const inventoryItems = await fetchInventoryItemsForDashboard();
        const activeItems = inventoryItems.filter(item => item.isActive !== false && !item.deletedAt);
        const totalProducts = activeItems.length;
        const lowStockItemsList = activeItems.filter(isLowStock);
        
        setStats(prev => ({ 
          ...prev, 
          totalProducts, 
          lowStockItems: lowStockItemsList.length 
        }));
        
        const sortedLowStockItems = [...lowStockItemsList]
          .sort((a, b) => (a.currentQuantity || 0) - (b.currentQuantity || 0))
          .slice(0, 10);
        
        setLowStockItems(sortedLowStockItems);
        setCategoryStates(prev => ({ ...prev, inventory: { loading: false, error: null, hasAccess: true } }));
      } catch (err) {
        console.error('Failed to load inventory stats:', err);
        const isPermissionError = err.response?.status === 403 || err.message?.includes('403');
        setCategoryStates(prev => ({ 
          ...prev, 
          inventory: { 
            loading: false, 
            error: isPermissionError ? null : (err.message || 'Failed to load inventory'), 
            hasAccess: !isPermissionError 
          } 
        }));
      }
    };

    // 2. Fetch Machine Data
    const loadMachines = async () => {
      if (!accessibleFeatures['Machine']) {
        setCategoryStates(prev => ({ ...prev, machine: { loading: false, error: null, hasAccess: false } }));
        return;
      }

      try {
        const machines = await fetchMachinesForDashboard();
        const activeMachinesCount = machines.filter(
          machine => machine.isActive !== false && !machine.deletedAt
        ).length;

        setStats(prev => ({ ...prev, activeMachines: activeMachinesCount }));
        setCategoryStates(prev => ({ ...prev, machine: { loading: false, error: null, hasAccess: true } }));
      } catch (err) {
        console.error('Failed to load machine stats:', err);
        const isPermissionError = err.response?.status === 403 || err.message?.includes('403');
        setCategoryStates(prev => ({ 
          ...prev, 
          machine: { 
            loading: false, 
            error: isPermissionError ? null : (err.message || 'Failed to load machines'), 
            hasAccess: !isPermissionError 
          } 
        }));
      }
    };

    // 3. Fetch Purchase Order Data
    const loadPurchaseOrders = async () => {
      if (!accessibleFeatures['Purchase Order']) {
        setCategoryStates(prev => ({ ...prev, purchaseOrder: { loading: false, error: null, hasAccess: false } }));
        return;
      }

      try {
        const [draftCount, appliedCount, recentData] = await Promise.all([
          fetchPurchaseOrderCount('DRAFT'),
          fetchPurchaseOrderCount('APPLIED'),
          fetchRecentPurchaseOrders()
        ]);

        setStats(prev => ({ ...prev, pendingPOs: draftCount + appliedCount }));
        
        const sortedRecentPOs = [...recentData]
          .sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at))
          .slice(0, 10);
        
        setRecentPOs(sortedRecentPOs);
        setCategoryStates(prev => ({ ...prev, purchaseOrder: { loading: false, error: null, hasAccess: true } }));
      } catch (err) {
        console.error('Failed to load PO stats:', err);
        const isPermissionError = err.response?.status === 403 || err.message?.includes('403');
        setCategoryStates(prev => ({ 
          ...prev, 
          purchaseOrder: { 
            loading: false, 
            error: isPermissionError ? null : (err.message || 'Failed to load purchase orders'), 
            hasAccess: !isPermissionError 
          } 
        }));
      }
    };

    // Run all fetches independently
    await Promise.all([
      loadInventory(),
      loadMachines(),
      loadPurchaseOrders()
    ]);

    setIsLoading(false);
  }, [accessibleFeatures]);

  useEffect(() => {
    calculateStats();
  }, [calculateStats]);

  return {
    stats,
    recentPOs,
    lowStockItems,
    isLoading,
    categoryStates,
    error,
    refetch: calculateStats
  };
};

